/**
 * Provider-agnostic usage accounting.
 *
 * Providers report token counts in wildly different shapes. Adapters call
 * `normalizeUsage` once and everything downstream (telemetry, metrics, cost)
 * works with the same `NormalizedTokenUsage` record.
 *
 * Nothing here talks to a vendor, a database, or the UI.
 */

import type { TokenUsage } from './types';

/**
 * Superset of the token dimensions we care about. `cached` and `reasoning`
 * are reserved: no provider reports them today, and every consumer treats
 * them as optional.
 */
export interface NormalizedTokenUsage extends TokenUsage {
  /** Prompt tokens served from a provider-side cache. Future. */
  cachedTokens?: number;
  /** Hidden reasoning tokens billed as output. Future. */
  reasoningTokens?: number;
  /** True when the counts were derived locally rather than reported. */
  estimated?: boolean;
}

/** Loose shape accepted from provider payloads before normalisation. */
export interface RawUsageLike {
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  input_tokens?: number;
  output_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  cachedTokens?: number;
  cached_tokens?: number;
  reasoningTokens?: number;
  reasoning_tokens?: number;
}

const num = (...values: Array<number | undefined>): number | undefined =>
  values.find((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0);

export const emptyUsage = (): NormalizedTokenUsage => ({
  promptTokens: 0,
  completionTokens: 0,
  totalTokens: 0,
});

/** Convert any provider usage payload into the canonical shape. */
export function normalizeUsage(raw: RawUsageLike | null | undefined): NormalizedTokenUsage {
  if (!raw) return emptyUsage();

  const promptTokens = num(raw.promptTokens, raw.input_tokens, raw.prompt_tokens) ?? 0;
  const completionTokens = num(raw.completionTokens, raw.output_tokens, raw.completion_tokens) ?? 0;
  const totalTokens = num(raw.totalTokens, raw.total_tokens) ?? promptTokens + completionTokens;
  const cachedTokens = num(raw.cachedTokens, raw.cached_tokens);
  const reasoningTokens = num(raw.reasoningTokens, raw.reasoning_tokens);

  return {
    promptTokens,
    completionTokens,
    totalTokens,
    ...(cachedTokens !== undefined ? { cachedTokens } : {}),
    ...(reasoningTokens !== undefined ? { reasoningTokens } : {}),
  };
}

/**
 * Very rough token estimate for providers that report nothing.
 * ~4 characters per token is the common industry heuristic; it is only used
 * for dashboards, never for billing.
 */
export const estimateTokens = (text: string): number =>
  text ? Math.max(1, Math.ceil(text.length / 4)) : 0;

/** Build an estimated usage record from raw prompt/completion strings. */
export function estimateUsage(prompt: string, completion = ''): NormalizedTokenUsage {
  const promptTokens = estimateTokens(prompt);
  const completionTokens = estimateTokens(completion);
  return {
    promptTokens,
    completionTokens,
    totalTokens: promptTokens + completionTokens,
    estimated: true,
  };
}

/** Sum usage records — used by metrics aggregation. */
export function addUsage(
  a: NormalizedTokenUsage,
  b: NormalizedTokenUsage | undefined,
): NormalizedTokenUsage {
  if (!b) return a;
  return {
    promptTokens: a.promptTokens + b.promptTokens,
    completionTokens: a.completionTokens + b.completionTokens,
    totalTokens: a.totalTokens + b.totalTokens,
    cachedTokens: (a.cachedTokens ?? 0) + (b.cachedTokens ?? 0),
    reasoningTokens: (a.reasoningTokens ?? 0) + (b.reasoningTokens ?? 0),
    estimated: a.estimated || b.estimated,
  };
}
